import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import useAuth from '../auth/useAuth';
import useIsMobile from '../auth/useIsMobile';
import usePlantScope from '../auth/usePlantScope';
import { listPlants } from '../services/plants';
import { listSuppliers } from '../services/suppliers';
import {
  computeTenureYears,
  deleteTeamMember,
  getTeamMember,
  listTeam,
  updateTeamMember,
} from '../services/team';
import { formatCountry, formatCurrency, formatDate, formatPhone } from '../utils/formatters';
import { displayFullName } from '../utils/teamDisplay';
import { liaisonManagersAtPlant } from '../utils/supplierScope';
import TeamForm from './TeamForm';

/**
 * Team-member detail page. Sections are gated to match the trust tiers:
 *
 *   Liaison only:       Basic (with QR), Plant assignment
 *   +Liaison Manager:   + Compensation (bill/pay rate), + Notes
 *   +Regional Manager:  + Employment (dates, biz entity), + Personal, + Emergency
 *   Director:           everything, and is the only role that can edit/delete
 *
 * Note: Liaison Managers see compensation but NOT employment dates — they
 * approve hours so they need rates, but contract dates and business entity
 * are HR-level detail reserved for Regional Managers and up.
 *
 * Plant cards are enriched from the Plants list so each assignment shows its
 * own OEM, region, country, state, and plant-side manager — no denormalized
 * copies on the team member's record to drift out of sync.
 */
export default function TeamMemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDirector, isRegionalManager, isLiaisonManager, name, email } =
    useAuth();
  // Mobile is view-only — hide Edit / Delete on phones.
  const isMobile = useIsMobile();
  // Plant-scoped users (Regional / Liaison Managers) can only click through to
  // plants assigned to them; other plant names render as plain text so they
  // never hit the "Plant Not Available" guard.
  const { scoped: plantScoped, allows: allowsPlant } = usePlantScope();
  const canEditHere = isDirector && !isMobile;

  // Contract/HR details — dates, business entity, tenure. RM + Director.
  const canSeeEmployment = isDirector || isRegionalManager;
  // Free-text notes — LM+ so on-site managers can leave each other context.
  const canSeeNotes = isDirector || isRegionalManager || isLiaisonManager;
  // Compensation and Personal (mailing address etc.) are scoped further
  // below — for Liaison Managers they only show on the LM's OWN assigned
  // liaisons (per Jillian, July 2026). See canSeeCompensation /
  // canSeeSensitive after the member + roster state loads.

  const [member, setMember] = useState(null);
  const [plants, setPlants] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const m = await getTeamMember(id);
      setMember(m);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Load plants whenever a member is shown (for the plant-card lookup) or
  // the edit modal opens (for the form's checklist).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listPlants();
        if (!cancelled) setPlants(rows || []);
      } catch {
        /* non-fatal; plant cards just fall back to the bare code */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [member?.id, editing]);

  // Suppliers — only needed to resolve the codes in a Liaison's Supplier
  // Assignment into names on the plant cards. Non-fatal: on failure the
  // cards fall back to showing the bare supplier codes.
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listSuppliers();
        if (!cancelled) setSuppliers(rows || []);
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [member?.id]);
  const supplierNameByCode = useMemo(() => {
    const map = new Map();
    for (const s of suppliers || []) {
      if (s.SupplierCode) {
        map.set(String(s.SupplierCode).toUpperCase().trim(), s.Title || s.SupplierCode);
      }
    }
    return map;
  }, [suppliers]);

  // Load the full team roster so we can build this member's reporting tree.
  // Direct reports are resolved by name-matching against other members'
  // RegionalManager (string) and liaisonManagers (string[]) fields, so we need
  // the whole list. Non-fatal: if this fails we just hide the Org card.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listTeam();
        if (!cancelled) setAllMembers(rows || []);
      } catch {
        /* non-fatal — the Org Structure card simply won't render */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [member?.id]);

  // Is the member being viewed one of the signed-in Liaison Manager's OWN
  // liaisons? True when the member's liaisonManagers list contains the
  // viewer's name. The viewer's roster record is resolved by Proxemics
  // email so nickname/display drift doesn't break the match; the raw MSAL
  // display name is kept as a fallback for viewers not on the roster.
  const isOwnLiaison = useMemo(() => {
    if (!member) return false;
    const lms = (member.liaisonManagers || [])
      .map((n) => String(n || '').trim().toLowerCase())
      .filter(Boolean);
    if (lms.length === 0) return false;
    const normEmail = (email || '').trim().toLowerCase();
    const viewerRecord = normEmail
      ? (allMembers || []).find(
          (m) => (m.Email || '').trim().toLowerCase() === normEmail,
        )
      : null;
    const candidates = [
      viewerRecord
        ? [viewerRecord.FirstName, viewerRecord.LastName]
            .filter(Boolean)
            .join(' ')
        : '',
      viewerRecord?.Title || '',
      name || '',
    ]
      .map((n) => String(n).trim().toLowerCase())
      .filter(Boolean);
    return candidates.some((n) => lms.includes(n));
  }, [member, allMembers, email, name]);

  // Viewing your own record — always fully visible to yourself (an LM's
  // own liaisonManagers list doesn't contain themselves, so without this
  // they'd lose sight of their own info here).
  const isSelf =
    !!member?.Email &&
    !!email &&
    member.Email.trim().toLowerCase() === email.trim().toLowerCase();

  // Pay / bill rates — LMs approve hours so they see rates, but ONLY for
  // their own assigned liaisons (per Jillian, July 2026). RM + Director
  // see everyone's.
  const canSeeCompensation =
    isDirector ||
    isRegionalManager ||
    (isLiaisonManager && (isOwnLiaison || isSelf));
  // Personal + emergency contact — most sensitive. RM + Director see
  // everyone's; an LM sees them only for their own assigned liaisons
  // (and themselves).
  const canSeeSensitive =
    isDirector ||
    isRegionalManager ||
    (isLiaisonManager && (isOwnLiaison || isSelf));
  // Role Designation & Duties — visible to everyone EXCEPT that an LM
  // only sees it on their own assigned liaisons and themselves (per
  // Jillian, July 2026).
  const canSeeRoleDuties =
    !isLiaisonManager || isOwnLiaison || isSelf;

  // Build the reporting tree(s) for the org-structure card.
  //
  // Default (single-tree) mode: the tree is rooted at the highest point in
  // this member's chain (their Regional Manager, or themselves if they are an
  // RM/Director) so Liaisons also see the shape of the team they belong to,
  // not just the people below them.
  //
  // Per-plant mode: when the viewed member is an RM or LM assigned to more
  // than one plant, we build one tree per plant — rooted on the member
  // themselves — with each tree's reports filtered to people also assigned
  // to that plant. This makes it clear that a manager covering multiple
  // plants may run distinct teams at each one.
  //
  // Only Active and On Leave members appear in the tree(s); Exited members
  // drop out.
  const orgData = useMemo(() => {
    if (!member) return null;
    const ACTIVE_STATUSES = ['Active', 'On Leave'];
    if (!ACTIVE_STATUSES.includes(member.EmploymentStatus)) return null;
    const role = member.PortalRole;
    if (!role || role === 'None') return null;

    const activeMembers = (allMembers || []).filter((m) =>
      ACTIVE_STATUSES.includes(m.EmploymentStatus),
    );

    const isManagerWithMultiplePlants =
      (role === 'RegionalManager' || role === 'LiaisonManager') &&
      Array.isArray(member.plantCodes) &&
      member.plantCodes.length > 1;

    if (isManagerWithMultiplePlants) {
      // One tree per assigned plant, rooted on this member.
      const perPlant = member.plantCodes
        .map((code) => ({
          code,
          tree: buildOrgTreeForPlant(member, activeMembers, code),
        }))
        // Hide plants where this manager has no team yet — nothing to show.
        .filter(({ tree }) => tree && tree.reports.length > 0);
      if (perPlant.length === 0) return null;
      return { mode: 'perPlant', perPlant };
    }

    const root = findOrgRoot(member, activeMembers);
    if (!root) return null;

    const tree = buildOrgTree(root, activeMembers);
    // Nothing meaningful to show if the tree is just this person with no
    // reports (e.g., a Liaison with no Regional Manager assigned).
    if (tree.member.id === member.id && tree.reports.length === 0) return null;
    return { mode: 'single', tree };
  }, [member, allMembers]);

  const plantByCode = useMemo(() => {
    const map = {};
    for (const p of plants) if (p.PlantCode) map[p.PlantCode] = p;
    return map;
  }, [plants]);

  const onSave = async (model) => {
    setSaving(true);
    setSaveError(null);
    try {
      await updateTeamMember(member.id, model);
      setEditing(false);
      await load();
    } catch (err) {
      console.error('[TeamMemberDetail] update failed', err);
      setSaveError(
        err?.body?.error?.message ||
          err?.message ||
          'Could not save changes. Check the browser console for details.',
      );
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    const name = displayFullName(member);
    if (!window.confirm(`Remove ${name} from the team list? This can't be undone.`)) {
      return;
    }
    await deleteTeamMember(member.id);
    navigate('/team');
  };

  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load This Team Member</div>
        <p>{error.message}</p>
        <button className="btn btn-outline btn-sm" onClick={load}>
          Retry
        </button>
      </div>
    );
  }
  if (!member) {
    return (
      <div className="card">
        <div className="card-title">Team Member Not Found</div>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/team')}>
          Back to team
        </button>
      </div>
    );
  }

  const tenure = computeTenureYears(member.StartDate, member.EndDate);
  const portalRoleLabel = formatPortalRole(member.PortalRole);
  const assignedPlants = (member.plantCodes || []).map((code) => ({
    code,
    plant: plantByCode[code],
  }));
  const subtitleBits = [
    member.Role || null,
    assignedPlants.map((a) => a.plant?.Title || a.code).filter(Boolean).join(', ') || null,
  ].filter(Boolean);

  return (
    <>
      <div className="page-header">
        <div>
          <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>
            <Link to="/team">Proxemics Team</Link> / {displayFullName(member) || '—'}
          </div>
          <h1 style={{ margin: '4px 0 0' }}>{displayFullName(member) || '—'}</h1>
          <div className="muted" style={{ marginTop: 4 }}>
            {subtitleBits.join(' · ')}
          </div>
        </div>
        <div className="actions">
          <button className="btn btn-outline" onClick={() => navigate('/team')}>
            ← Team
          </button>
          {canEditHere && (
            <button className="btn btn-primary" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
      </div>

      {/* ---- Basic info (everyone) ------------------------------------
          Identity only — Designation / Portal Role / duty flags live in
          the Role Designation & Duties card below so this page mirrors
          the Team form's section order. QR code floats in the top-right
          of the card (when present) at half its former size, with no
          label — the image reads as a QR without annotation. */}
      <div className="card">
        <div className="card-title">Basic Info</div>
        {/* QR sits directly under the heading, then the identity fields
            below it. Previously the QR floated top-right and overlapped the
            email/phone values on narrow (phone) widths. */}
        {member.QRImage && (
          <div
            style={{
              background: '#fff',
              padding: 6,
              borderRadius: 8,
              border: '1px solid var(--brand-border, #e5e7eb)',
              width: 'fit-content',
              marginBottom: 12,
            }}
          >
            <img
              src={member.QRImage}
              alt="QR code"
              style={{ width: 100, height: 100, objectFit: 'contain', display: 'block' }}
            />
          </div>
        )}
        {member.Nickname && <Field label="Nickname" value={member.Nickname} />}
        <Field label="Proxemics Email" value={emailLink(member.Email)} />
        <Field label="Phone" value={formatPhone(member.Phone)} />
      </div>

      {/* ---- Role Designation & Duties ---------------------------------
          Mirrors the same-named section on the Team form. Business Entity
          renders whenever a value is stored, so legacy records whose
          designation has since changed don't silently lose the entity
          name. Duty flags render a "Yes" row only when true — members
          who don't send reports or submit timesheets don't get noisy
          "No" lines. LMs only see this card for their own assigned
          liaisons and themselves (canSeeRoleDuties). */}
      {canSeeRoleDuties && (
      <div className="card">
        <div className="card-title">Role Designation & Duties</div>
        <Field label="Designation" value={member.EmploymentType} />
        {member.BusinessEntity && (
          <Field label="Business Entity" value={member.BusinessEntity} />
        )}
        {portalRoleLabel && <Field label="Portal Role" value={portalRoleLabel} />}
        {/* Portal access status — manually tracked on the Team form.
            Shows where this person stands on actually using the portal
            (license + Entra assignment), since most employees are on
            email-only Microsoft plans and never sign in. */}
        <Field
          label="Portal Access"
          value={<PortalAccessBadge status={member.PortalAccessStatus} />}
        />
        {member.SendsReports && <Field label="Sends Reports" value="Yes" />}
        {member.SubmitsTimesheets && <Field label="Submits Billing Tracker" value="Yes" />}
      </div>
      )}

      {/* ---- Employment (RM / Director) -------------------------------
          Mirrors the Team form's Employment section. Business Entity is
          in the Role Designation & Duties card above (since that's where
          Designation lives and entity depends on designation). */}
      {canSeeEmployment && (
        <div className="card">
          <div className="card-title">Employment</div>
          <Field label="Employment Status" value={member.EmploymentStatus} />
          <Field
            label="Start Date"
            value={
              member.StartDate ? (
                <>
                  {formatDate(member.StartDate)}
                  {tenure != null && (
                    <span className="muted" style={{ marginLeft: 10 }}>
                      · {tenure} year{tenure === 1 ? '' : 's'} with Proxemics
                    </span>
                  )}
                </>
              ) : (
                ''
              )
            }
          />
          <Field label="End Date" value={formatDate(member.EndDate)} />
        </div>
      )}

      {/* ---- Plant assignment (everyone) ------------------------------ */}
      <div className="card">
        <div className="card-title">Plant Assignment</div>
        {assignedPlants.length === 0 && (
          <p className="muted" style={{ marginTop: 0 }}>
            No plants assigned.
          </p>
        )}
        {assignedPlants.map(({ code, plant }) => (
          <div
            key={code}
            style={{
              border: '1px solid var(--brand-border, #e5e7eb)',
              borderRadius: 8,
              padding: '10px 12px',
              marginTop: 8,
            }}
          >
            <div style={{ fontWeight: 600 }}>
              {plantScoped && !allowsPlant(code) ? (
                <span title="You don't have access to this plant">
                  {plant?.Title || code}
                </span>
              ) : (
                <Link
                  to={`/plants/${code}`}
                  style={{ color: 'var(--brand-primary, #E87722)' }}
                >
                  {plant?.Title || code}
                </Link>
              )}
              <span className="muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
                {code}
              </span>
            </div>
            <Field label="OEM" value={plant?.OEM} />
            <Field label="Region" value={deriveRegion(plant)} />
            <Field label="Country" value={formatCountry(plant?.Country)} />
            <Field label="State / Province" value={plant?.State} />
            {/* Vehicle and Program lists, derived from the plant's
                vehicles array. Multi-vehicle plants render their
                entries comma-joined. Each on its own labeled row so
                they read at a glance like the address fields above. */}
            <Field label="Vehicle" value={vehicleListForPlant(plant)} />
            <Field label="Program" value={programListForPlant(plant)} />
            {/* Supplier Assignment — Liaisons (per Jillian, July 2026):
                the supplier(s) this liaison supports at this plant.
                Liaison Managers (Sep 2026): the supplier(s) they bill
                here when the plant is shared with another LM; a sole LM
                gets every supplier automatically. Names resolved once
                the suppliers list is loaded. */}
            {member.PortalRole === 'Liaison' &&
              (member.supplierAssignments?.[code] || []).length > 0 && (
                <Field
                  label="Supplier Assignment"
                  value={(member.supplierAssignments[code] || [])
                    .map(
                      (sc) =>
                        supplierNameByCode.get(
                          String(sc || '').toUpperCase().trim(),
                        ) || sc,
                    )
                    .join(', ')}
                />
              )}
            {member.PortalRole === 'LiaisonManager' &&
              (() => {
                const others = liaisonManagersAtPlant(allMembers, code).filter(
                  (m) => String(m.id) !== String(member.id),
                );
                const picks = (member.supplierAssignments?.[code] || []).filter(Boolean);
                if (others.length === 0) {
                  return (
                    <Field
                      label="Supplier Assignment"
                      value="All suppliers at this plant (only Liaison Manager here)"
                    />
                  );
                }
                if (picks.length === 0) {
                  return (
                    <Field
                      label="Supplier Assignment"
                      value={
                        <span style={{ color: 'var(--brand-danger)' }}>
                          None assigned. This plant is shared with{' '}
                          {others.map((m) => displayFullName(m)).join(', ')}, so
                          this manager's Billing Tracker shows no suppliers here
                          until some are checked on their profile.
                        </span>
                      }
                    />
                  );
                }
                return (
                  <Field
                    label="Supplier Assignment"
                    value={picks
                      .map(
                        (sc) =>
                          supplierNameByCode.get(
                            String(sc || '').toUpperCase().trim(),
                          ) || sc,
                      )
                      .join(', ')}
                  />
                );
              })()}
          </div>
        ))}
      </div>

      {/* ---- Org structure ------------------------------------------ */}
      {/* Only show when the member is assigned to at least one plant.
          Directors and other plant-less roles don't have a meaningful
          org tree to render — the plant-context is what makes the
          tree make sense. */}
      {(member.plantCodes || []).length > 0 && (
      <div className="card">
        <div className="card-title">Org Structure</div>
        <Field label="Regional Manager" value={member.RegionalManager} />
        <Field
          label={`Liaison Manager${(member.liaisonManagers || []).length === 1 ? '' : 's'}`}
          value={
            (member.liaisonManagers || []).length
              ? member.liaisonManagers.join(', ')
              : ''
          }
        />
        {orgData?.mode === 'single' && (
          <div style={{ marginTop: 16 }}>
            <OrgNode node={orgData.tree} level={0} highlightId={member.id} />
          </div>
        )}
        {orgData?.mode === 'perPlant' && (
          <div style={{ marginTop: 16 }}>
            {orgData.perPlant.map(({ code, tree }) => {
              const plant = plantByCode[code];
              return (
                <div
                  key={code}
                  style={{
                    border: '1px solid var(--brand-border, #e5e7eb)',
                    borderRadius: 8,
                    padding: '12px 14px',
                    marginTop: 12,
                    background: '#fff',
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      color: 'var(--brand-muted, #64748b)',
                      marginBottom: 10,
                    }}
                  >
                    {plantScoped && !allowsPlant(code) ? (
                      <span title="You don't have access to this plant">
                        {plant?.Title || code}
                      </span>
                    ) : (
                      <Link
                        to={`/plants/${code}`}
                        style={{ color: 'var(--brand-primary, #E87722)' }}
                      >
                        {plant?.Title || code}
                      </Link>
                    )}
                    {plant?.Title && (
                      <span style={{ marginLeft: 8, fontWeight: 500 }}>
                        {code}
                      </span>
                    )}
                  </div>
                  <OrgNode node={tree} level={0} highlightId={member.id} />
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* ---- Compensation (LM / RM / Director) ------------------------
          Pay Rate is conceptually part of Employment but LMs approve hours
          so they're allowed to see rates. Sits here — just above the
          sensitive Personal / Emergency Contact cards — so the rate sits
          next to the other restricted fields rather than in the middle
          of the everyone-visible stack. */}
      {canSeeCompensation && (
        <div className="card">
          <div className="card-title">Compensation</div>
          <Field label="Pay Rate" value={formatCurrency(member.PayRate)} />
        </div>
      )}

      {/* ---- Personal (RM / Director) --------------------------------- */}
      {canSeeSensitive && (
        <div className="card">
          <div className="card-title">Personal</div>
          {/* Prefer the new structured columns when present; fall back to
              the legacy free-text MailingAddress for older records. */}
          <Field
            label="Mailing Address"
            value={buildMailingAddress(member) || member.MailingAddress}
            multiline
          />
          <Field label="Personal Email" value={emailLink(member.PersonalEmail)} />
          <Field label="Birthdate" value={formatDate(member.Birthdate)} />
          {/* Shirt fit + size are listed under Birthdate so apparel-gear
              details sit with the rest of the member's personal info.
              Safety vest follows right after, matching the order on the
              Team form and the My Account profile tiles. */}
          <Field label="Shirt Fit" value={member.ShirtFit} />
          <Field label="Shirt Size" value={member.ShirtSize} />
          <Field label="Safety Vest Size" value={member.VestSize} />
          <Field
            label="Safety Vest Color"
            value={
              member.VestColor === 'Other'
                ? member.VestColorOther || 'Other'
                : member.VestColor
            }
          />
        </div>
      )}

      {/* ---- Emergency contact (RM / Director) ------------------------ */}
      {canSeeSensitive &&
        (member.EmergencyContactName ||
          member.EmergencyContactPhone ||
          member.EmergencyContactRelation) && (
          <div className="card">
            <div className="card-title">Emergency Contact</div>
            <Field label="Name" value={member.EmergencyContactName} />
            <Field label="Phone" value={formatPhone(member.EmergencyContactPhone)} />
            <Field label="Relation" value={member.EmergencyContactRelation} />
          </div>
        )}

      {/* ---- Notes (LM / RM / Director) ------------------------------- */}
      {canSeeNotes && member.Notes && (
        <div className="card">
          <div className="card-title">Notes</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{member.Notes}</div>
        </div>
      )}

      {/* ---- Delete (Director only) ----------------------------------- */}
      {/* Delete lives at the bottom of the page on purpose so it's hard
          to hit by accident while scanning the top of the record. Inline
          layout matches the "Delete Part" pattern on PartForm: red-outlined
          button on the left, muted instructional text on the right. For
          people who've actually left Proxemics, Employment Status → Exited
          is the correct path so tenure / history stays intact. */}
      {canEditHere && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginTop: 16,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            className="btn btn-outline"
            onClick={onDelete}
            style={{
              color: 'var(--brand-danger)',
              borderColor: 'var(--brand-danger)',
              flexShrink: 0,
            }}
          >
            Delete Team Member
          </button>
          <span className="muted" style={{ fontSize: 12, lineHeight: 1.3 }}>
            Only delete team member if added by mistake. If team member is no
            longer with Proxemics, change their Employment Status to Exited.
          </span>
        </div>
      )}

      <Modal
        open={editing}
        onClose={() => (saving ? null : setEditing(false))}
        title={`Edit ${displayFullName(member) || 'Team Member'}`}
      >
        {editing && (
          <>
            {saveError && (
              <div
                className="error"
                style={{
                  marginBottom: 12,
                  padding: '8px 12px',
                  background: '#fde8e8',
                  color: '#b91c1c',
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                {saveError}
              </div>
            )}
            <TeamForm
              initial={member}
              plants={plants}
              team={allMembers}
              saving={saving}
              onSave={onSave}
              onCancel={() => setEditing(false)}
            />
          </>
        )}
      </Modal>
    </>
  );
}

// ---- small presentational helpers ----------------------------------------

function Field({ label, value, multiline }) {
  const empty = value == null || value === '';
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        margin: '6px 0',
        alignItems: multiline ? 'flex-start' : 'baseline',
      }}
    >
      <div style={{ minWidth: 180, color: 'var(--brand-muted, #64748b)' }}>{label}</div>
      <div style={{ flex: 1, whiteSpace: multiline ? 'pre-wrap' : 'normal' }}>
        {empty ? <span className="muted">—</span> : value}
      </div>
    </div>
  );
}

function emailLink(email) {
  if (!email) return '';
  return <a href={`mailto:${email}`}>{email}</a>;
}

// Legal-form name used by the org-structure matchers below, which compare
// against the RegionalManager and liaisonManagers fields — both stored as
// "FirstName LastName" in their canonical (non-nickname) form. For display,
// use displayFullName() from utils/teamDisplay instead so nicknames show.
function fullName(m) {
  if (!m) return '';
  return [m.FirstName, m.LastName].filter(Boolean).join(' ') || m.Title || '';
}

function formatPortalRole(r) {
  if (!r || r === 'None') return null;
  if (r === 'RegionalManager') return 'Regional Manager';
  if (r === 'LiaisonManager') return 'Liaison Manager';
  return r;
}

/**
 * Pill badge for the manually-tracked Portal Access status. Green =
 * Active, amber = Pending Setup, gray = No Access. Blank legacy
 * records read as No Access, since that's the default reality for
 * most employees (email-only Microsoft plans).
 */
function PortalAccessBadge({ status }) {
  const s = status || 'No Access';
  const palette =
    s === 'Active'
      ? { bg: 'rgba(22, 163, 74, 0.12)', fg: 'var(--brand-success, #16a34a)' }
      : s === 'Pending Setup'
        ? { bg: 'rgba(202, 138, 4, 0.12)', fg: '#ca8a04' }
        : { bg: 'rgba(100, 116, 139, 0.15)', fg: '#475569' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 10px',
        background: palette.bg,
        color: palette.fg,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {s}
    </span>
  );
}

// Combine the structured address columns into a multiline string. Returns
// '' if none of them are set — the caller then falls back to the legacy
// free-text MailingAddress column.
function buildMailingAddress(m) {
  if (!m) return '';
  const street = (m.MailingStreet || '').trim();
  const city = (m.MailingCity || '').trim();
  const state = (m.MailingState || '').trim();
  const zip = (m.MailingZip || '').trim();
  const country = formatCountry(m.MailingCountry);
  if (!(street || city || state || zip || country)) return '';
  const cityLine = [city, state, zip].filter(Boolean).join(', ');
  return [street, cityLine, country].filter(Boolean).join('\n');
}

// Plants don't currently store Region directly — best-effort derive from
// the Country so the plant card doesn't go blank.
function deriveRegion(plant) {
  const c = (plant?.Country || '').trim().toLowerCase();
  if (!c) return '';
  if (['usa', 'us', 'united states', 'canada', 'mexico'].includes(c)) {
    return 'North America';
  }
  return '';
}

// ---- Org structure helpers ----------------------------------------------

// Names are stored as free text on two different fields (RegionalManager,
// liaisonManagers[]), so matching is string-based. Normalize to lowercase
// and collapse whitespace so "Alex Smith" == "alex smith" == "Alex  Smith".
function normalizeName(s) {
  return (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

// Figure out where the org-structure tree should be rooted for the given
// member. We want Liaisons and Liaison Managers to see the *full* team they
// belong to, not just the empty space below them — so we climb to their
// Regional Manager. Directors and Regional Managers are already at or near
// the top, so they root on themselves.
//
// If we can't find the Regional Manager in the active roster (e.g. their RM
// is Exited or was never set), fall back to rooting on the member themselves.
function findOrgRoot(member, activeMembers) {
  const role = member?.PortalRole;
  if (!role || role === 'None') return null;
  if (role === 'Director' || role === 'RegionalManager') return member;
  const rmName = normalizeName(member.RegionalManager);
  if (rmName) {
    const rm = (activeMembers || []).find(
      (m) =>
        m.PortalRole === 'RegionalManager' && normalizeName(fullName(m)) === rmName,
    );
    if (rm) return rm;
  }
  return member;
}

// Build the reporting tree rooted at `member`. Direct reports are resolved
// by role:
//   Director        → every RegionalManager in the team
//   RegionalManager → members whose RegionalManager field == this member's name
//   LiaisonManager  → members whose liaisonManagers array contains this name
//   Liaison         → no direct reports
// A `visited` set guards against cycles (e.g. self-referential manager fields
// from bad data). The caller is responsible for passing an already-filtered
// roster (Active/On Leave only) — this function doesn't filter further.
function buildOrgTree(member, allMembers, visited) {
  const seen = visited || new Set();
  if (!member || seen.has(member.id)) {
    return { member, reports: [] };
  }
  seen.add(member.id);

  const myName = normalizeName(fullName(member));
  const role = member.PortalRole;
  let reports = [];

  if (role === 'Director') {
    reports = (allMembers || []).filter(
      (m) => m.id !== member.id && m.PortalRole === 'RegionalManager',
    );
  } else if (role === 'RegionalManager') {
    reports = (allMembers || []).filter((m) => {
      if (m.id === member.id) return false;
      if (normalizeName(m.RegionalManager) !== myName) return false;
      // A Liaison whose Liaison Manager is also a direct report of this RM
      // will appear nested under that LM below — skip them here so they
      // don't render twice in the same tree.
      if (m.PortalRole === 'Liaison') {
        const lms = (m.liaisonManagers || [])
          .map((n) => normalizeName(n))
          .filter(Boolean);
        if (lms.length > 0) {
          const hasLmUnderThisRm = (allMembers || []).some(
            (other) =>
              other.PortalRole === 'LiaisonManager' &&
              normalizeName(other.RegionalManager) === myName &&
              lms.includes(normalizeName(fullName(other))),
          );
          if (hasLmUnderThisRm) return false;
        }
      }
      return true;
    });
  } else if (role === 'LiaisonManager') {
    reports = (allMembers || []).filter((m) => {
      if (m.id === member.id) return false;
      const lms = Array.isArray(m.liaisonManagers) ? m.liaisonManagers : [];
      return lms.some((lm) => normalizeName(lm) === myName);
    });
  }

  // Sort by last name, then first, so the tree is stable regardless of the
  // order rows came back from SharePoint.
  reports.sort((a, b) => {
    const la = normalizeName(a.LastName);
    const lb = normalizeName(b.LastName);
    if (la !== lb) return la.localeCompare(lb);
    return normalizeName(a.FirstName).localeCompare(normalizeName(b.FirstName));
  });

  return {
    member,
    reports: reports.map((r) => buildOrgTree(r, allMembers, seen)),
  };
}

// Like buildOrgTree but constrained to a single plant: only team members
// whose plantCodes include `plantCode` appear in the reports chain. Used by
// the per-plant view for managers covering multiple plants, where each plant
// may have its own distinct team.
function buildOrgTreeForPlant(member, allMembers, plantCode, visited) {
  const seen = visited || new Set();
  if (!member || seen.has(member.id)) {
    return { member, reports: [] };
  }
  seen.add(member.id);

  const atPlant = (m) =>
    Array.isArray(m.plantCodes) && m.plantCodes.includes(plantCode);
  const myName = normalizeName(fullName(member));
  const role = member.PortalRole;
  let reports = [];

  if (role === 'Director') {
    reports = (allMembers || []).filter(
      (m) =>
        m.id !== member.id && m.PortalRole === 'RegionalManager' && atPlant(m),
    );
  } else if (role === 'RegionalManager') {
    reports = (allMembers || []).filter((m) => {
      if (m.id === member.id) return false;
      if (!atPlant(m)) return false;
      if (normalizeName(m.RegionalManager) !== myName) return false;
      // Same de-dup as buildOrgTree: a Liaison whose LM also reports to this
      // RM (and also covers this plant) should nest under the LM, not here.
      if (m.PortalRole === 'Liaison') {
        const lms = (m.liaisonManagers || [])
          .map((n) => normalizeName(n))
          .filter(Boolean);
        if (lms.length > 0) {
          const hasLmUnderThisRm = (allMembers || []).some(
            (other) =>
              other.PortalRole === 'LiaisonManager' &&
              atPlant(other) &&
              normalizeName(other.RegionalManager) === myName &&
              lms.includes(normalizeName(fullName(other))),
          );
          if (hasLmUnderThisRm) return false;
        }
      }
      return true;
    });
  } else if (role === 'LiaisonManager') {
    reports = (allMembers || []).filter((m) => {
      if (m.id === member.id) return false;
      if (!atPlant(m)) return false;
      const lms = Array.isArray(m.liaisonManagers) ? m.liaisonManagers : [];
      return lms.some((lm) => normalizeName(lm) === myName);
    });
  }

  reports.sort((a, b) => {
    const la = normalizeName(a.LastName);
    const lb = normalizeName(b.LastName);
    if (la !== lb) return la.localeCompare(lb);
    return normalizeName(a.FirstName).localeCompare(normalizeName(b.FirstName));
  });

  return {
    member,
    reports: reports.map((r) =>
      buildOrgTreeForPlant(r, allMembers, plantCode, seen),
    ),
  };
}

function OrgNode({ node, level, highlightId }) {
  const { member, reports } = node;
  const roleLabel = formatPortalRole(member.PortalRole);
  const isHighlighted = member.id === highlightId;
  return (
    <div style={{ marginLeft: level === 0 ? 0 : 20, marginTop: level === 0 ? 0 : 6 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          border: `${isHighlighted ? '2px' : '1px'} solid ${
            isHighlighted ? 'var(--brand-primary, #E87722)' : 'var(--brand-border, #e5e7eb)'
          }`,
          borderRadius: 8,
          marginBottom: 6,
          background: level === 0 ? '#f8fafc' : '#fff',
        }}
      >
        <Link
          to={`/team/${member.id}`}
          style={{ fontWeight: 600, color: 'var(--brand-primary, #1d4ed8)' }}
        >
          {displayFullName(member) || member.Title || '—'}
        </Link>
        {roleLabel && (
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--brand-muted, #64748b)',
              background: '#e2e8f0',
              borderRadius: 999,
              padding: '2px 8px',
            }}
          >
            {roleLabel}
          </span>
        )}
        {member.Role && (
          <span className="muted" style={{ fontSize: 12 }}>
            · {member.Role}
          </span>
        )}
      </div>
      {reports.length > 0 && (
        <div
          style={{
            borderLeft: '2px solid var(--brand-border, #e5e7eb)',
            marginLeft: 10,
            paddingLeft: 10,
          }}
        >
          {reports.map((child) => (
            <OrgNode
              key={child.member.id}
              node={child}
              level={level + 1}
              highlightId={highlightId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Comma-joined list of vehicle names (Model, falling back to legacy
 * single-string `name` or "Make Model"). Empty string for plants
 * with no vehicles, which the Field component renders as an em-dash.
 */
function vehicleListForPlant(plant) {
  if (!plant) return '';
  const seen = new Set();
  const out = [];
  for (const v of plant.vehicles || []) {
    const label =
      v?.model ||
      v?.name ||
      [v?.make, v?.model].filter(Boolean).join(' ');
    const trimmed = (label || '').trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out.join(', ');
}

/**
 * Comma-joined list of distinct program labels across the plant's
 * vehicles. Falls back to the legacy `modelYear` key for plants saved
 * before the rename.
 */
function programListForPlant(plant) {
  if (!plant) return '';
  const seen = new Set();
  const out = [];
  for (const v of plant.vehicles || []) {
    const raw = (v?.program || v?.modelYear || '').trim();
    if (!raw || seen.has(raw)) continue;
    seen.add(raw);
    out.push(raw);
  }
  return out.join(', ');
}
